import React, { useState, useEffect } from 'react';

import {
  IconUser,
  IconSchool,
  IconMoodKid,
  IconUserCircle,
} from '@tabler/icons-react';

import Select from '@/components/Select';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getOverviewStats,
  getUserGrowthGraph,
  getRecentActivities,
  getSchoolEngagementGraph,
} from '@/service/dashboardService';
import {
  TOverviewStats,
  TRecentActivityItem,
  TUserGrowthGraphItem,
  TSchoolEngagementGraphItem,
} from '@/types';
import { formatLastActive } from '@/utils';

import TopSchoolsEngagementChart from './components/TopSchoolsEngagementChart';
import UserGrowthTrendsChart from './components/UserGrowthTrendsChart';

const SUMMARY_CARD_CONFIG: {
  key: keyof TOverviewStats;
  icon: React.ReactNode;
  label: string;
}[] = [
  {
    key: 'totalUsers',
    icon: <IconUserCircle color="#717784" />,
    label: 'dashboard.summary.total-users',
  },
  {
    key: 'activeParents',
    icon: <IconUser color="#717784" />,
    label: 'dashboard.summary.active-parents',
  },
  {
    key: 'totalSchools',
    icon: <IconSchool color="#717784" />,
    label: 'dashboard.summary.total-schools',
  },
  {
    key: 'activeStudents',
    icon: <IconMoodKid color="#717784" />,
    label: 'dashboard.summary.active-students',
  },
];

const GROWTH_PERIOD_FILTER_MAP: Record<string, number> = {
  yearly: 1,
  monthly: 2,
  weekly: 3,
};

// Dropdown options
const GROWTH_PERIOD_OPTIONS = [
  { value: 'yearly', label: 'dashboard.filters.yearly' },
  { value: 'monthly', label: 'dashboard.filters.monthly' },
  { value: 'weekly', label: 'dashboard.filters.weekly' },
];

const ENGAGEMENT_PERIOD_OPTIONS = [
  { value: 'weekly', label: 'dashboard.filters.weekly' },
  { value: 'monthly', label: 'dashboard.filters.monthly' },
  { value: 'yearly', label: 'dashboard.filters.yearly' },
];

const ENGAGEMENT_PERIOD_FILTER_MAP: Record<string, number> = {
  yearly: 1,
  monthly: 2,
  weekly: 3,
};

const Dashboard = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();
  const [tableData, setTableData] = useState<TRecentActivityItem[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [overviewStats, setOverviewStats] = useState<TOverviewStats | null>(
    null
  );
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const [growthPeriod, setGrowthPeriod] = useState('yearly');
  const [engagementPeriod, setEngagementPeriod] = useState('yearly');
  const [userGrowthData, setUserGrowthData] = useState<TUserGrowthGraphItem[]>(
    []
  );
  const [schoolEngagementData, setSchoolEngagementData] = useState([]);
  const pageTitle = formatText('dashboard.title');

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: pageTitle, to: '/' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  useEffect(() => {
    const fetchOverviewStats = async () => {
      setIsOverviewLoading(true);
      const res = await getOverviewStats();
      if (res?.data) {
        setOverviewStats(res?.data);
      }
      setIsOverviewLoading(false);
    };
    fetchOverviewStats();
  }, []);

  useEffect(() => {
    const fetchUserGrowthGraph = async () => {
      const filter = GROWTH_PERIOD_FILTER_MAP[growthPeriod] ?? 1;
      const res = await getUserGrowthGraph(filter);
      if (res?.data && Array.isArray(res.data)) {
        setUserGrowthData(res.data);
      } else {
        setUserGrowthData([]);
      }
    };
    fetchUserGrowthGraph();
  }, [growthPeriod]);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      setIsTableLoading(true);
      const res = await getRecentActivities();
      if (res?.data && Array.isArray(res.data)) {
        setTableData(res.data);
      } else {
        setTableData([]);
      }
      setIsTableLoading(false);
    };
    fetchRecentActivities();
  }, []);

  useEffect(() => {
    const fetchSchoolEngagementGraph = async () => {
      const filter = ENGAGEMENT_PERIOD_FILTER_MAP[engagementPeriod] ?? 1;
      const res = await getSchoolEngagementGraph(filter);
      if (res?.data && Array.isArray(res.data)) {
        setSchoolEngagementData(
          res.data?.map((item: TSchoolEngagementGraphItem) => ({
            ...item,
            label: item?.name,
          }))
        );
      } else {
        setSchoolEngagementData([]);
      }
    };
    fetchSchoolEngagementGraph();
  }, [engagementPeriod]);

  // Table columns (recent activities)
  const columns = [
    {
      title: 'dashboard.table.user',
      key: 'userName',
      width: '20%',
      render: (row: TRecentActivityItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.userName ?? '-'}
        </span>
      ),
    },
    {
      title: 'dashboard.table.activity',
      key: 'activity',
      width: '40%',
      render: (row: TRecentActivityItem) => (
        <span className="text-sm text-neutral-secondary">
          {row?.activity ?? '-'}
        </span>
      ),
    },
    {
      title: 'dashboard.table.module',
      key: 'module',
      width: '20%',
      render: (row: TRecentActivityItem) => (
        <span className="text-sm text-neutral-secondary">
          {row?.module ?? '-'}
        </span>
      ),
    },
    {
      title: 'dashboard.table.time',
      key: 'created_at',
      width: '20%',
      render: (row: TRecentActivityItem) => (
        <span className="text-sm text-neutral-secondary">
          {formatLastActive(row?.created_at, formatText)}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {SUMMARY_CARD_CONFIG?.map((stat) => {
          const item = overviewStats?.[stat.key];
          return (
            <SummaryCard
              key={stat?.key}
              variant="dashboard"
              label={formatText(stat?.label)}
              value={(item?.total ?? 0).toLocaleString()}
              icon={stat?.icon}
              trendDirection={item?.trend ?? 'STABLE'}
              isPositive={item?.isPositive ?? true}
              trendLabel={formatText('common.this-month')}
              trend={`${item?.percentage ?? 0}%`}
              isLoading={isOverviewLoading}
            />
          );
        })}
      </div>

      {/* User Growth Trends Chart */}
      <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between mb-1 gap-2">
          <h2 className="text-lg font-semibold text-neutral-primary">
            {formatText('dashboard.charts.user-growth-trends')}
          </h2>
          <Select
            options={GROWTH_PERIOD_OPTIONS}
            selected={growthPeriod}
            onChange={(val) => setGrowthPeriod(val as string)}
            className="w-36! py-2!"
            isTextFormat
          />
        </div>
        <UserGrowthTrendsChart data={userGrowthData} />
      </div>

      {/* Top 5 Schools by Engagement Chart */}
      <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between mb-1 gap-2">
          <h2 className="text-lg font-semibold text-neutral-primary">
            {formatText('dashboard.charts.top-schools-engagement')}
          </h2>
          <Select
            options={ENGAGEMENT_PERIOD_OPTIONS}
            selected={engagementPeriod}
            onChange={(val) => setEngagementPeriod(val as string)}
            className="w-36! py-2!"
            isTextFormat
          />
        </div>
        <TopSchoolsEngagementChart data={schoolEngagementData} />
      </div>

      {/* Recent Activities Table */}
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold">
          {formatText('dashboard.recent-activities')}
        </h3>
        <CustomTable
          isLoading={isTableLoading}
          dataSource={tableData}
          columns={columns}
          hidePagination={true}
          isEmpty={!isTableLoading && tableData?.length === 0}
          emptyTitle={formatText('dashboard.no-activities-title')}
          emptyDescription={formatText('dashboard.no-activities-desc')}
          rootClassName="h-auto!"
        />
      </div>
    </div>
  );
};

export default Dashboard;
